/**
 * useLocale — 언어 전환 (언어 목록 DB 기반 무제한 확장) 훅.
 *
 * 【역할】
 * - 앱 전역 locale 전환 관문 — localStorage + i18n + 백엔드 라벨/메뉴/탭까지 일괄 갱신.
 * - Locale 후보 목록을 백엔드 /locales 에서 동적 수신하여 DB 에 등록된 모든 언어 자동 지원.
 *
 * 【주요 기능】
 * - setLocale(loc): 7 단계 전환 플로우 (아래 참조)
 * - useSupportedLocales(): DB 등록 지원 언어 목록 구독 (fallback: ko/en/zh/ja)
 * - useI18nTickStore: 언어 전환 후 강제 re-render 용 카운터 store
 * - LOCALE_CHANGE_EVENT: 다른 훅 (useMessage/useCodes) 이 캐시 무효화 시 구독
 *
 * 【내부 상태 / 캐시 구조】
 * - useLocalesStore: 서버 /locales 응답 캐시 (loaded 후 재호출 skip).
 * - useI18nTickStore: tick 증가로 useSyncExternalStore 구독자 강제 갱신.
 *
 * 【setLocale 플로우】
 * 1) localStorage 저장 → axios 인터셉터 X-Locale 헤더
 * 2) i18n.changeLanguage
 * 3) 백엔드 fw_label 재로드 (addResourceBundle)
 * 4) DB 영속화 — PATCH /auth/locale (로그인 상태일 때만)
 * 5) /auth/me 재조회 → 메뉴 트리 갱신 + 탭 refreshLabels
 * 6) tick 증가 (강제 re-render)
 * 7) LOCALE_CHANGE_EVENT 이벤트 dispatch
 *
 * 【사용 예시】
 * ```tsx
 * const { locale, setLocale } = useLocale()
 * const locales = useSupportedLocales()
 * <Select value={locale} onChange={setLocale} options={locales.map(l => ({ value: l.locale, label: l.nativeName }))} />
 * ```
 *
 * 【왜 이 패턴?】
 * - i18next 단독으로는 "서버 라벨 리로드 + DB 영속화 + 메뉴 다국어" 를 조합할 수 없음.
 * - Option B 로 DB 기반 동적 언어 목록 채택 — 신규 locale 추가 시 프런트 배포 불필요.
 */
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { create } from 'zustand'
import http from './api/axios'
import { useMenuStore, type MenuNode } from '@/store/menuStore'
import { useTabStore } from '@/store/tabStore'

/** Locale 코드 — 'ko'/'en'/'zh-CN'/'ja' 등 DB 에 등록된 어떤 키라도 허용. */
export type Locale = string

/** 백엔드 /locales 응답 엔트리 — UI 렌더링 메타 포함. */
export interface LocaleInfo {
  locale: string
  name: string
  nativeName?: string
  flagEmoji?: string
  sortOrder?: number
  isDefault?: number | boolean
}

/** DB 미연결 시 fallback — 로그인 전/API 장애 시 최소 보장 */
const FALLBACK_LOCALES: LocaleInfo[] = [
  {
    locale: 'ko',
    name: 'Korean',
    nativeName: '한국어',
    flagEmoji: '🇰🇷',
    sortOrder: 10,
    isDefault: 1
  },
  { locale: 'en', name: 'English', nativeName: 'English', flagEmoji: '🇺🇸', sortOrder: 20 },
  { locale: 'zh', name: 'Chinese', nativeName: '中文', flagEmoji: '🇨🇳', sortOrder: 30 },
  { locale: 'ja', name: 'Japanese', nativeName: '日本語', flagEmoji: '🇯🇵', sortOrder: 40 }
]

/** (하위 호환) 코드가 이 배열을 참조할 때 fallback locale key 만 반환 */
export const SUPPORTED_LOCALES: Locale[] = FALLBACK_LOCALES.map((l) => l.locale)

export const LOCALE_CHANGE_EVENT = 'fw:locale-change'

/**
 * 전역 지원 언어 store — /api/locales 로 동적 수신.
 * loaded=true 이후 재호출은 무시하여 네트워크 낭비 방지.
 */
interface LocalesState {
  locales: LocaleInfo[]
  loaded: boolean
  loading: boolean
  load: () => Promise<void>
}

export const useLocalesStore = create<LocalesState>((set, get) => ({
  locales: FALLBACK_LOCALES,
  loaded: false,
  loading: false,
  load: async () => {
    if (get().loading || get().loaded) return
    set({ loading: true })
    try {
      const res = await http.get('/locales')
      const data = res.data?.data ?? res.data
      if (Array.isArray(data) && data.length > 0) {
        set({ locales: data as LocaleInfo[], loaded: true })
      }
    } catch {
      // fallback 유지
    } finally {
      set({ loading: false })
    }
  }
}))

/** 지원 언어 목록 구독 훅. 초기 마운트 시 1회 서버 조회. */
export function useSupportedLocales(): LocaleInfo[] {
  const locales = useLocalesStore((s) => s.locales)
  const load = useLocalesStore((s) => s.load)
  useEffect(() => {
    void load()
  }, [load])
  return locales
}

/** i18n tick store — locale 변경 시 증가 */
interface I18nTickState {
  tick: number
  incrementTick: () => void
}
export const useI18nTickStore = create<I18nTickState>((set) => ({
  tick: 0,
  incrementTick: () => set((s) => ({ tick: s.tick + 1 }))
}))

/**
 * 메뉴 트리에서 path 에 해당하는 라벨을 재귀 탐색.
 * @returns 찾으면 label 문자열, 없으면 null.
 */
type LabelLookupNode = {
  path?: string
  menuPath?: string
  label?: string
  menuName?: string
  children?: LabelLookupNode[]
}

function findLabelInTree(nodes: MenuNode[] | LabelLookupNode[], path: string): string | null {
  if (!Array.isArray(nodes)) return null
  for (const n of nodes as LabelLookupNode[]) {
    if (n.path === path || n.menuPath === path) return String(n.label ?? n.menuName ?? '')
    if (n.children && n.children.length) {
      const found = findLabelInTree(n.children, path)
      if (found) return found
    }
  }
  return null
}

/**
 * useLocale — 현재 locale + setLocale 전환 함수.
 * @returns { locale, setLocale, supported }
 */
export function useLocale() {
  const { i18n } = useTranslation()
  const current = (i18n.language as Locale) ?? 'ko'

  /**
   * 언어 전환 본체 — 7단계 (플로우 L1 주석 참조).
   * 동일 locale 재설정 시 즉시 early-return.
   */
  const setLocale = useCallback(
    async (loc: Locale) => {
      if (loc === current) return
      localStorage.setItem('fw.locale', loc)
      await i18n.changeLanguage(loc)

      // fw_label 번들 재로드
      try {
        const labelsRes = await http.get('/labels', { params: { locale: loc } })
        const bundle = labelsRes.data?.data ?? labelsRes.data ?? {}
        if (bundle && typeof bundle === 'object') {
          i18n.addResourceBundle(loc, 'translation', bundle, true, true)
          try {
            ;(i18n as { emit?: (ev: string, ...args: unknown[]) => void }).emit?.(
              'languageChanged',
              loc
            )
          } catch {
            /* noop */
          }
        }
      } catch {
        /* noop */
      }

      // DB 영속화 — 로그인 된 경우만 (401/익명 요청은 무시)
      try {
        if (localStorage.getItem('fw.accessToken')) {
          await http.patch('/auth/locale', { locale: loc })
        }
      } catch {
        /* noop — 실패해도 UI locale 은 이미 전환됨 */
      }

      // /auth/me 재조회 → 메뉴/사용자 정보 다국어 갱신
      try {
        const meRes = await http.get('/auth/me')
        const menus = meRes.data?.data?.menus
        if (Array.isArray(menus)) {
          useMenuStore.getState().setTreeFromBackend(menus)
          const tree = useMenuStore.getState().tree
          useTabStore.getState().refreshLabels((path) => findLabelInTree(tree, path) ?? path)
        }
      } catch {
        /* noop */
      }

      useI18nTickStore.getState().incrementTick()

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(LOCALE_CHANGE_EVENT, { detail: loc }))
      }
    },
    [i18n, current]
  )

  return { locale: current, setLocale, supported: SUPPORTED_LOCALES }
}
